function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import EventEmitter from 'eventemitter3';
import { WhiteboardPermissionFlag } from './WhitePermissions';
import { WhiteboardKeys } from './WhiteboardKeys';
import { Gesture } from './Gesture';
import { removeObserver } from '@netless/forge-room';
export class Camera extends EventEmitter {
  get inherentMatrix() {
    const inherentMatrix = new this.scope.Matrix();
    inherentMatrix.scale(this.inherentScale, [0, 0]);
    return inherentMatrix;
  }
  get translateScale() {
    const view = this.scope.project.view;
    return 1 / view.matrix.scaling.x;
  }
  constructor(initSize, maxScale, dom, userManager, scope, whiteboardAttrsMap, hasPermission, requestUserMap,
  // paper.view 的可见区域尺寸, 房间里所有人的可见区域尺寸是一样的, 但是 css 尺寸可能不一样
  _paperSize,
  // canvas 元素的 css 尺寸
  _domSize) {
    super();
    _defineProperty(this, "scope", void 0);
    _defineProperty(this, "dom", void 0);
    _defineProperty(this, "lastTriggerTime", 0);
    _defineProperty(this, "lastDelta", {
      x: 0,
      y: 0
    });
    _defineProperty(this, "requestUserMap", void 0);
    _defineProperty(this, "userManager", void 0);
    _defineProperty(this, "observers", new Map());
    _defineProperty(this, "whiteboardAttrsMap", void 0);
    _defineProperty(this, "paperSize", void 0);
    _defineProperty(this, "domSize", void 0);
    _defineProperty(this, "hasPermission", void 0);
    _defineProperty(this, "gesture", void 0);
    _defineProperty(this, "inherentScale", 1);
    _defineProperty(this, "maxScale", void 0);
    _defineProperty(this, "initSize", void 0);
    _defineProperty(this, "bound", void 0);
    _defineProperty(this, "boundTiemoutId", void 0);
    _defineProperty(this, "enableByMouse", true);
    _defineProperty(this, "enableByTouch", true);
    _defineProperty(this, "boundaryColor", '#F44336');
    _defineProperty(this, "enableBoundaryHighlight", true);
    _defineProperty(this, "handleMainCameraChange", evt => {
      const userMap = this.requestUserMap(this.userManager.selfId);
      const cameraMode = userMap.get(WhiteboardKeys.cameraMode);
      if (cameraMode !== 'main') {
        return;
      }
      for (const [key, value] of evt.changes.keys.entries()) {
        if (key === WhiteboardKeys.viewMatrix) {
          if (value.action === 'add' || value.action === 'update') {
            const matrixValue = this.whiteboardAttrsMap.get(WhiteboardKeys.viewMatrix);
            const matrix = new this.scope.Matrix(matrixValue);
            userMap.set(WhiteboardKeys.viewMatrix, matrixValue);
            this.emit('zoom', this.inherentMatrix.appended(matrix));
          }
        }
      }
    });
    _defineProperty(this, "handleUserLeave", user => {
      if (user.id === this.userManager.selfId) {
        return;
      }
      const cb = this.observers.get(user.id);
      if (cb) {
        removeObserver(this.requestUserMap(user.id), cb);
      }
    });
    _defineProperty(this, "handleUserJoin", user => {
      if (this.observers.has(user.id)) {
        const cb = this.observers.get(user.id);
        if (cb) {
          removeObserver(this.requestUserMap(user.id), cb);
          this.observers.delete(user.id);
        }
      }
      const observer = evt => {
        this.handleViewMatrixUpdate(user.id, evt);
      };
      this.requestUserMap(user.id).observe(observer);
      this.observers.set(user.id, observer);
    });
    _defineProperty(this, "handleViewMatrixUpdate", (userId, evt) => {
      const cameraMode = this.requestUserMap(this.userManager.selfId).get(WhiteboardKeys.cameraMode);
      for (const [key, value] of evt.changes.keys.entries()) {
        if (value.action === 'update') {
          if (key === WhiteboardKeys.viewMatrix) {
            if (userId !== this.userManager.selfId && cameraMode === userId) {
              const targetMatrixValue = this.requestUserMap(userId).get(WhiteboardKeys.viewMatrix);
              const target = new this.scope.Matrix(targetMatrixValue);
              this.requestUserMap(this.userManager.selfId).set(WhiteboardKeys.viewMatrix, targetMatrixValue);
              this.emit('zoom', this.inherentMatrix.appended(target));
            } else if (userId === this.userManager.selfId && cameraMode === 'free') {
              const currentMatrixValue = this.requestUserMap(userId).get(WhiteboardKeys.viewMatrix);
              const current = new this.scope.Matrix(currentMatrixValue);
              this.emit('zoom', this.inherentMatrix.appended(current));
            }
          } else if (key === WhiteboardKeys.currentPage) {
            if (value.action === 'update' || value.action === 'add') {
              this.emit('userPageChange', userId, this.requestUserMap(userId).get(WhiteboardKeys.currentPage));
            }
          } else if (key === WhiteboardKeys.cameraMode) {
            if (value.action === 'update' || value.action === 'add') {
              this.emit('userViewModeChange', userId, this.requestUserMap(userId).get(WhiteboardKeys.cameraMode), value.oldValue);
            }
          }
        }
      }
    });
    _defineProperty(this, "handleWheel", evt => {
      if (!this.enableByMouse) {
        return;
      }
      evt.preventDefault();
      evt.stopImmediatePropagation();
      evt.stopPropagation();
      const domSize = this.domSize();
      const paperSize = this.paperSize();

      // 相机的坐标计算，以白板的固有尺寸为基准
      const inherentDeltaY = evt.deltaY / domSize[1] * paperSize[1];
      const inherentDeltaX = evt.deltaX / domSize[0] * paperSize[0];
      if (Date.now() - this.lastTriggerTime < 32) {
        this.lastDelta.y += inherentDeltaY;
        this.lastDelta.x += inherentDeltaX;
        return;
      }
      if (evt.ctrlKey) {
        // 双指捏合
        // @ts-ignore
        const wheelDelta = Math.sign(evt.wheelDelta || 100) * 120;
        const paperPoint = this.scope.project.view.getEventPoint(evt);
        const deltaY = inherentDeltaY + this.lastDelta.y;
        const matrix = new this.scope.Matrix();
        let scale = 1;
        if (deltaY > 0) {
          scale = Math.max(1 - Math.abs(deltaY / wheelDelta), 0.00000001);
        } else {
          // 放大
          scale = 1 + Math.abs(deltaY / wheelDelta);
        }
        scale = Math.max(0.9, scale);
        scale = Math.min(1.1, scale);
        matrix.scale(scale, paperPoint);
        this.updateViewMatrix(matrix);
      } else {
        // 拖动
        const deltaX = this.lastDelta.x + evt.deltaX;
        const deltaY = this.lastDelta.y + evt.deltaY;
        const matrix = new this.scope.Matrix();
        matrix.translate({
          x: -deltaX * window.devicePixelRatio,
          y: -deltaY * window.devicePixelRatio
        });
        this.updateViewMatrix(matrix);
      }
      this.lastTriggerTime = Date.now();
      this.lastDelta = {
        x: 0,
        y: 0
      };
    });
    this.maxScale = maxScale;
    this.bound = window.document.createElement('div');
    this.bound.style.cssText = 'transition: box-shadow 100ms;pointer-events:none;z-index:99;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);';
    this.initSize = initSize;
    this.hasPermission = hasPermission;
    this.paperSize = _paperSize;
    this.domSize = _domSize;
    this.whiteboardAttrsMap = whiteboardAttrsMap;
    this.scope = scope;
    this.requestUserMap = requestUserMap;
    this.userManager = userManager;
    this.dom = dom;
    this.dom.appendChild(this.bound);
    this.dom.addEventListener('wheel', this.handleWheel, {
      passive: false,
      capture: true
    });
    this.dom.addEventListener('touchstart', e => {
      e.preventDefault();
    }, {
      passive: false
    });
    this.createModel(this.userManager.selfId);
    this.userManager.userIdList().forEach(userId => {
      const observer = evt => {
        this.handleViewMatrixUpdate(userId, evt);
      };
      if (this.observers.has(userId)) {
        removeObserver(this.requestUserMap(userId), observer);
      }
      this.requestUserMap(userId).observe(observer);
      this.observers.set(userId, observer);
    });
    this.userManager.on('join', this.handleUserJoin);
    this.userManager.on('leave', this.handleUserLeave);
    if (!this.whiteboardAttrsMap.has(WhiteboardKeys.viewMatrix)) {
      this.whiteboardAttrsMap.set(WhiteboardKeys.viewMatrix, [1, 0, 0, 1, 0, 0]);
    }
    this.whiteboardAttrsMap.observe(this.handleMainCameraChange);
    this.gesture = new Gesture(this.dom, this.scope);
    this.gesture.on('offset', (x, y) => {
      if (!this.enableByTouch) {
        return;
      }
      const matrix = new this.scope.Matrix();
      matrix.translate({
        x: x * this.translateScale,
        y: y * this.translateScale
      });
      this.updateViewMatrix(matrix);
    });
    this.gesture.on('scale', (scale, center) => {
      if (!this.enableByTouch) {
        return;
      }
      const matrix = new this.scope.Matrix();
      const paperPoint = this.scope.project.view.viewToProject(center);
      matrix.scale(scale, paperPoint);
      this.updateViewMatrix(matrix);
    });
  }
  highlightBound() {
    if (this.enableBoundaryHighlight) {
      this.bound.style.boxShadow = `inset 0px 0px 6px 2px ${this.boundaryColor}`;
      window.clearTimeout(this.boundTiemoutId);
      this.boundTiemoutId = window.setTimeout(() => {
        this.bound.style.boxShadow = 'none';
      }, 100);
    }
  }
  updateInitSize(size) {
    this.initSize = size;
  }
  updateInherentScale(scale) {
    this.inherentScale = scale;
  }
  getInherentScale() {
    return this.inherentScale;
  }
  getActiveMatrix() {
    const cameraMode = this.requestUserMap(this.userManager.selfId).get(WhiteboardKeys.cameraMode);
    let matrixValue;
    if (cameraMode === 'main') {
      matrixValue = this.whiteboardAttrsMap.get(WhiteboardKeys.viewMatrix);
    } else if (cameraMode === 'free') {
      matrixValue = this.requestUserMap(this.userManager.selfId).get(WhiteboardKeys.viewMatrix);
    } else {
      matrixValue = this.requestUserMap(cameraMode).get(WhiteboardKeys.viewMatrix);
    }
    return new this.scope.Matrix(matrixValue);
  }
  triggerZoom() {
    const [width, height] = this.domSize();
    this.bound.style.width = `${width}px`;
    this.bound.style.height = `${height}px`;
    this.emit('zoom', this.inherentMatrix.appended(this.getActiveMatrix()));
  }
  resetViewMatrixToFlow(userId) {
    const matrixValue = this.requestUserMap(userId).get(WhiteboardKeys.viewMatrix);
    const matrix = new this.scope.Matrix(matrixValue);
    this.emit('zoom', this.inherentMatrix.appended(matrix));
  }
  resetViewMatrixToMain() {
    const matrixValue = this.whiteboardAttrsMap.get(WhiteboardKeys.viewMatrix);
    const matrix = new this.scope.Matrix(matrixValue);
    this.emit('zoom', this.inherentMatrix.appended(matrix));
  }
  createModel(userId) {
    const userMap = this.requestUserMap(userId);
    if (!userMap.has(WhiteboardKeys.viewMatrix)) {
      userMap.set(WhiteboardKeys.viewMatrix, [1, 0, 0, 1, 0, 0]);
    }
    if (!userMap.has(WhiteboardKeys.cameraMode)) {
      userMap.set(WhiteboardKeys.cameraMode, 'main');
    }
  }
  validNextMatrix(next) {
    let shouldHighlightBound = false;
    const maxTranslate = (this.maxScale - 1) / 2;
    const maxScale = maxTranslate * 2 + 1;
    const minScale = 1 / maxScale;
    if (next.a > maxScale) {
      const tx = next.tx / next.a;
      next.a = maxScale;
      next.tx = tx * maxScale;
      shouldHighlightBound = true;
    }
    if (next.a < minScale) {
      const tx = next.tx / next.a;
      next.a = minScale;
      next.tx = tx * minScale;
      shouldHighlightBound = true;
    }
    if (next.d > maxScale) {
      const ty = next.ty / next.d;
      next.d = maxScale;
      next.ty = ty * maxScale;
      shouldHighlightBound = true;
    }
    if (next.d < minScale) {
      const ty = next.ty / next.d;
      next.d = minScale;
      next.ty = ty * minScale;
      shouldHighlightBound = true;
    }
    const maxTranslateX = this.initSize.width * maxTranslate;
    if (next.tx / next.a > maxTranslateX) {
      next.tx = maxTranslateX * next.a;
      shouldHighlightBound = true;
    }
    const minTx = (this.initSize.width * (maxTranslate + 1) - this.scope.project.view.bounds.width) * -next.a;
    if (next.tx < minTx) {
      next.tx = minTx;
      shouldHighlightBound = true;
    }
    const maxTranslateY = this.initSize.height * maxTranslate;
    if (next.ty / next.d > maxTranslateY) {
      next.ty = maxTranslateY * next.d;
      shouldHighlightBound = true;
    }
    const minTy = (this.initSize.height * (maxTranslate + 1) - this.scope.project.view.bounds.height) * -next.d;
    if (next.ty < minTy) {
      next.ty = minTy;
      shouldHighlightBound = true;
    }
    if (shouldHighlightBound) {
      this.highlightBound();
    }
    return next;
  }
  updateViewMatrix(matrix) {
    let append = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    const userMap = this.requestUserMap(this.userManager.selfId);
    const cameraMode = userMap.get(WhiteboardKeys.cameraMode);
    let next = matrix;
    if (append) {
      const currentMatrixValue = userMap.get(WhiteboardKeys.viewMatrix);
      const current = new this.scope.Matrix(currentMatrixValue);
      next = current.appended(matrix);
    }
    if (this.maxScale >= 1) {
      next = this.validNextMatrix(next);
    } else {
      if (!(next.scaling.x <= 3 && next.scaling.x >= 0.2) && append) {
        return;
      }
    }
    if (cameraMode === 'free' || cameraMode === 'main') {
      userMap.set(WhiteboardKeys.viewMatrix, [next.a, next.b, next.c, next.d, next.tx, next.ty]);
    }
    if (cameraMode === 'main') {
      // 拥有主视角权限才能修改
      if (this.hasPermission(WhiteboardPermissionFlag.mainView)) {
        this.whiteboardAttrsMap.set(WhiteboardKeys.viewMatrix, [next.a, next.b, next.c, next.d, next.tx, next.ty]);
      }
    }
  }
  scale(value) {
    let origin = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'center';
    const matrix = new this.scope.Matrix();
    if (origin === 'center') {
      matrix.scale(value, this.scope.project.view.bounds.center);
    } else if (origin === 'top-left') {
      matrix.scale(value, [0, 0]);
    }
    this.updateViewMatrix(matrix);
  }
  translate(offsetX, offsetY) {
    const matrix = new this.scope.Matrix();
    matrix.translate({
      x: offsetX,
      y: offsetY
    });
    this.updateViewMatrix(matrix);
  }
  reset() {
    this.updateViewMatrix(new this.scope.Matrix(), false);
    this.lastDelta = {
      x: 0,
      y: 0
    };
  }
  dispose() {
    Array.from(this.observers.keys()).forEach(userId => {
      const cb = this.observers.get(userId);
      if (cb) {
        removeObserver(this.requestUserMap(userId), cb);
      }
    });
    removeObserver(this.whiteboardAttrsMap, this.handleMainCameraChange);
    this.userManager.off('join', this.handleUserJoin);
    this.userManager.off('leave', this.handleUserLeave);
    this.gesture.removeAllListeners();
    this.removeAllListeners();
  }
}